// apps/web/src/components/painel/home/assinatura-areas.tsx — Fase 17.8, Plano 20 (D-05, D-11; UI-SPEC U-22, U-24).
// Painel "A empresa hoje · por área" da Diretoria (no lugar da esteira): grade de até 6 áreas em 2 colunas, cada uma um link (ou um bloco
// sem link, quando a sessão não abre a rota) com o ponto do matiz da área (--k, de painel-perfis.css), o nome (Body 600) e o estado de
// uma linha. A área com alerta troca o ponto para --color-warning, ganha AlertTriangle e o estado em âmbar (a cor nunca é o único
// sinal: o ícone e o texto "atenção" para leitor de tela). Abaixo, a 32px, "Maiores clientes no mês" (`RankingClientes`): até 4 barras
// de 6px no gradiente do perfil, com o valor à direita (R$ compacto ou pç) e o vazio com Receipt. Nenhum valor em R$ vem sem a
// permissão: o servidor já devolve o ranking por peças quando a sessão não vê valores.
import { useId, type ReactElement } from 'react';
import { AlertTriangle, Layers, Receipt } from 'lucide-react';
import type { AreaEmpresa, RankingCliente, Regiao } from '@pieltec/contracts';
import { podeAbrirRota } from '../../../lib/painel/blocos';
import { formatarMoedaCompacta, formatarNumero } from '../../../lib/painel/home/formato';
import { EstadoVazio } from './estado-vazio';
import { RegiaoCarregando, RegiaoErro } from './regiao-estado';
import '../../../styles/painel-home.css';

export const TEXTO_SEM_FATURAMENTO = 'Ainda não há faturamento neste mês.';
export const TEXTO_SEM_PECAS_DEVOLVIDAS = 'Ainda não há peças devolvidas neste mês.';
export const TEXTO_SEM_AREAS = 'Nenhuma área liberada para o seu perfil.';

/** Linhas do ranking (a API já devolve até 4). */
export const LINHAS_DO_RANKING = 4;

const NOME_DA_AREA: Readonly<Record<AreaEmpresa['id'], string>> = {
  producao: 'Produção',
  vendas: 'Vendas',
  qualidade: 'Qualidade',
  estoque: 'Estoque',
  compras: 'Compras',
  expedicao: 'Expedição',
};

/** Fração da barra (0 a 1, três casas) de um cliente sobre o maior valor da lista; valor zero, negativo ou ilegível não tem barra. */
export function barraDoRanking(valor: number | string, maior: number): number {
  const numero = typeof valor === 'number' ? valor : Number(valor);
  if (!Number.isFinite(numero) || numero <= 0 || !Number.isFinite(maior) || maior <= 0) return 0;
  return Math.min(1, Math.round((numero / maior) * 1000) / 1000);
}

type RegiaoRanking = Regiao<{ readonly criterio: 'faturado' | 'pecas'; readonly clientes: readonly RankingCliente[] }>;

export interface RankingClientesProps {
  readonly ranking: RegiaoRanking;
  readonly onTentarNovamente?: () => void;
}

const numeroDe = (valor: number | string): number => (typeof valor === 'number' ? valor : Number(valor));

/** "R$ 58,4 mil" (faturado, string decimal do servidor) ou "3.480 pç" (peças devolvidas). */
function textoDoValor(valor: number | string, criterio: 'faturado' | 'pecas'): string {
  return criterio === 'faturado' ? formatarMoedaCompacta(String(valor)) : `${formatarNumero(numeroDe(valor))} pç`;
}

/** "Maiores clientes no mês": até 4 barras; indisponível tem nova tentativa e sem medição não renderiza nada. */
export function RankingClientes({ ranking, onTentarNovamente }: RankingClientesProps): ReactElement | null {
  const idTitulo = useId();
  if (ranking.estado === 'SEM_MEDICAO') return null;
  const subtitulo = ranking.estado === 'MEDIDO' ? (ranking.criterio === 'faturado' ? 'faturado' : 'peças devolvidas') : undefined;
  const cabecalho = (
    <header className="painel-assinatura__cabecalho">
      <h3 id={idTitulo} className="painel-assinatura__titulo">
        Maiores clientes no mês
      </h3>
      {subtitulo !== undefined && <span className="painel-assinatura__subtitulo">{subtitulo}</span>}
    </header>
  );
  if (ranking.estado === 'INDISPONIVEL') {
    return (
      <section className="painel-assinatura painel-ranking-secao" data-testid="painel-ranking" aria-labelledby={idTitulo}>
        {cabecalho}
        <RegiaoErro id="ranking" nome="os maiores clientes do mês" onTentarNovamente={onTentarNovamente ?? (() => undefined)} />
      </section>
    );
  }
  const visiveis = ranking.clientes.slice(0, LINHAS_DO_RANKING);
  const maior = Math.max(0, ...visiveis.map((cliente) => numeroDe(cliente.valor)).filter((valor) => Number.isFinite(valor)));
  return (
    <section className="painel-assinatura painel-ranking-secao" data-testid="painel-ranking" aria-labelledby={idTitulo}>
      {cabecalho}
      {visiveis.length === 0 ? (
        <EstadoVazio icone={Receipt} texto={ranking.criterio === 'faturado' ? TEXTO_SEM_FATURAMENTO : TEXTO_SEM_PECAS_DEVOLVIDAS} />
      ) : (
        <ul className="painel-ranking">
          {visiveis.map((cliente) => {
            const texto = textoDoValor(cliente.valor, ranking.criterio);
            return (
              <li key={cliente.clienteId} className="painel-ranking__linha" data-testid="painel-ranking-linha">
                <div className="painel-ranking__topo">
                  <span className="painel-ranking__nome">{cliente.nome}</span>
                  <span className="painel-ranking__valor">{texto}</span>
                </div>
                <div className="painel-ranking__trilho" role="img" aria-label={`${cliente.nome}: ${texto}`}>
                  <div className="painel-ranking__barra" style={{ transform: `scaleX(${barraDoRanking(cliente.valor, maior)})` }} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function Area({ area, permissoes }: { readonly area: AreaEmpresa; readonly permissoes: readonly string[] }): ReactElement {
  const nome = NOME_DA_AREA[area.id];
  const corpo = (
    <>
      <span className="painel-area__ponto" aria-hidden="true" />
      <span className="painel-area__texto">
        <span className="painel-area__nome">{nome}</span>
        <span className={area.alerta ? 'painel-area__estado painel-area__estado--alerta' : 'painel-area__estado'}>
          {area.alerta && <AlertTriangle className="painel-area__icone size-3" aria-hidden="true" />}
          {area.alerta && <span className="sr-only">atenção: </span>}
          {area.estado}
        </span>
      </span>
    </>
  );
  const atributos = { 'data-area': area.id, 'data-testid': `painel-area-${area.id}`, ...(area.alerta ? { 'data-alerta': 'true' } : {}) } as const;
  return podeAbrirRota(area.href, permissoes) ? (
    <a className="painel-area" href={area.href} {...atributos}>
      {corpo}
    </a>
  ) : (
    <div className="painel-area" {...atributos}>
      {corpo}
    </div>
  );
}

export interface AssinaturaAreasProps {
  readonly areas: readonly AreaEmpresa[];
  readonly ranking: RegiaoRanking;
  readonly permissoes: readonly string[];
  readonly onTentarNovamente?: () => void;
}

function CabecalhoDoPainel({ idTitulo }: { readonly idTitulo: string }): ReactElement {
  return (
    <header className="painel-lista__cabecalho">
      <h2 id={idTitulo} className="painel-lista__titulo">
        <span className="painel-lista__marcador" aria-hidden="true" />
        A empresa hoje
      </h2>
      <span className="painel-areas__subtitulo">por área</span>
    </header>
  );
}

/** O painel inteiro: grade das áreas e, 32px abaixo, o ranking de clientes. */
export function AssinaturaAreas({ areas, ranking, permissoes, onTentarNovamente }: AssinaturaAreasProps): ReactElement {
  const idTitulo = useId();
  return (
    <section className="painel-esteira painel-areas" data-testid="painel-areas" aria-labelledby={idTitulo}>
      <CabecalhoDoPainel idTitulo={idTitulo} />
      <div className="painel-areas__corpo">
        {areas.length === 0 ? (
          <EstadoVazio icone={Layers} texto={TEXTO_SEM_AREAS} />
        ) : (
          <ul className="painel-areas__grade">
            {areas.map((area) => (
              <li key={area.id} className="painel-areas__item">
                <Area area={area} permissoes={permissoes} />
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="painel-esteira__assinatura">
        <RankingClientes ranking={ranking} onTentarNovamente={onTentarNovamente} />
      </div>
    </section>
  );
}

export interface PainelAreasProps {
  /** `null` enquanto a carga está em andamento (esqueleto). */
  readonly regiao: Regiao<{ readonly tipo: 'areas'; readonly areas: readonly AreaEmpresa[]; readonly ranking: RegiaoRanking }> | null;
  readonly permissoes: readonly string[];
  readonly onTentarNovamente?: () => void;
}

/** Os estados do painel: carregando (esqueleto), indisponível (região de erro) e sem medição (nada). */
export function PainelAreas({ regiao, permissoes, onTentarNovamente }: PainelAreasProps): ReactElement | null {
  const idTitulo = useId();
  if (regiao === null) {
    return (
      <section className="painel-esteira painel-areas" data-testid="painel-areas" aria-labelledby={idTitulo} aria-busy="true">
        <CabecalhoDoPainel idTitulo={idTitulo} />
        <RegiaoCarregando id="areas" altura={160} />
      </section>
    );
  }
  if (regiao.estado === 'SEM_MEDICAO') return null;
  if (regiao.estado === 'INDISPONIVEL') {
    return (
      <section className="painel-esteira painel-areas" data-testid="painel-areas" aria-labelledby={idTitulo}>
        <CabecalhoDoPainel idTitulo={idTitulo} />
        <div className="painel-lista__erro">
          <RegiaoErro id="assinatura" nome="a empresa hoje" onTentarNovamente={onTentarNovamente ?? (() => undefined)} />
        </div>
      </section>
    );
  }
  return <AssinaturaAreas areas={regiao.areas} ranking={regiao.ranking} permissoes={permissoes} onTentarNovamente={onTentarNovamente} />;
}
